/**
 * Navigation for a world that is being destroyed while you walk through it.
 *
 * There is deliberately no graph here. A* over cells would be wrong twice over:
 * the grid is millions of cells, and any path it produced would be invalidated
 * the moment someone set the floor on fire. Instead every enemy re-decides its
 * next step from a handful of probes into the live cell grid. That costs a fixed
 * number of `peek` calls per tick and is *always* correct about the world as it
 * is right now, which is the only property that matters here.
 *
 * The three primitives:
 *   1. ground-follow  — probe ahead for walls, ledges and gaps
 *   2. jump-arc feasibility — closed-form, against the movement package numbers
 *      in docs/02-movement.md §2.2, so AI jumps obey the same physics as yours
 *   3. dig test — `M.hardness` vs the body's dig power, for burrowers/breachers
 *
 * Everything writes into module-level result singletons. Nothing here allocates,
 * because this runs for every enemy every tick.
 */

import { M, VOID, type MatId } from '../../sim/materials';
import { Category } from '../../sim/materials';
import type { CellWorld } from '../../sim/world';
import { BLOCK_SHIFT, BLOCK_SIZE, isPassable, type StructureMap } from '../../world/structure';

// ---------------------------------------------------------------- constants

/**
 * The player's movement package (docs/02-movement.md §2.2). Enemy jump arcs are
 * evaluated against these same constants scaled per-enemy, so "can it follow me
 * up there?" has an answer the player can predict from their own movement.
 */
export const MOVE = {
  maxRunSpeed: 145,
  jumpHeight: 54,
  jumpApexTime: 0.34,
  riseGravity: 934,
  fallGravity: 1495,
  apexGravity: 560,
  maxFallSpeed: 620,
  /** Derived: v = 2h/t. The impulse that produces `jumpHeight` under riseGravity. */
  jumpSpeed: 318,
  cornerCorrection: 4,
  ledgeSnap: 3,
} as const;

/** How far ahead ground-follow will look for the far side of a gap. */
const MAX_GAP_SCAN = 14;
/** How far down it will look before calling a drop bottomless. */
const MAX_DROP_SCAN = 40;
/** Wall height (cells) it will bother measuring before giving up. */
const MAX_WALL_SCAN = 24;

export const NavReason = {
  CLEAR: 0,
  STEP_UP: 1,
  WALL_JUMPABLE: 2,
  WALL_DIGGABLE: 3,
  WALL_BLOCKED: 4,
  GAP_JUMPABLE: 5,
  GAP_TOO_WIDE: 6,
  DROP_SAFE: 7,
  DROP_BOTTOMLESS: 8,
  ARRIVED: 9,
} as const;
export type NavReasonId = (typeof NavReason)[keyof typeof NavReason];

// ------------------------------------------------------------ probe budget

/**
 * A per-entity allowance of cell reads. Twenty enemies × unbounded probing is
 * how a navigation system quietly becomes the frame budget, so the caller hands
 * each enemy a fixed number of reads and the probes degrade gracefully (they
 * report "unknown, assume blocked") when it runs out.
 */
export class ProbeBudget {
  limit: number;
  left: number;
  /** Total probes spent since the last `resetTotals()` — telemetry only. */
  spent = 0;

  constructor(limit = 48) {
    this.limit = limit;
    this.left = limit;
  }

  reset(limit = this.limit): void {
    this.limit = limit;
    this.left = limit;
  }

  /** Returns false when the budget is exhausted; callers must handle that. */
  take(n = 1): boolean {
    if (this.left < n) return false;
    this.left -= n;
    this.spent += n;
    return true;
  }

  get exhausted(): boolean {
    return this.left <= 0;
  }
}

// --------------------------------------------------------------- nav result

export interface NavResult {
  /** Desired horizontal drive, -1..1. */
  moveX: number;
  /** Desired vertical drive for fliers/swimmers, -1..1. Ignored by walkers. */
  moveY: number;
  jump: boolean;
  /** Vertical impulse to apply if `jump` — already sized for the obstacle. */
  jumpSpeed: number;
  dig: boolean;
  digX: number;
  digY: number;
  /** No route in the requested direction. Callers should turn or give up. */
  blocked: boolean;
  reason: NavReasonId;
}

/**
 * Reused across all callers. Read it before the next nav call — there is
 * exactly one navigator running at a time (single-threaded, one entity per
 * step), so a singleton is safe and saves 20 allocations per tick.
 */
export const NAV: NavResult = {
  moveX: 0,
  moveY: 0,
  jump: false,
  jumpSpeed: 0,
  dig: false,
  digX: 0,
  digY: 0,
  blocked: false,
  reason: NavReason.CLEAR,
};

function resetNav(): void {
  NAV.moveX = 0;
  NAV.moveY = 0;
  NAV.jump = false;
  NAV.jumpSpeed = 0;
  NAV.dig = false;
  NAV.digX = 0;
  NAV.digY = 0;
  NAV.blocked = false;
  NAV.reason = NavReason.CLEAR;
}

/** Locomotion capabilities of one body. Held per-def, passed by reference. */
export interface NavCaps {
  /** Upward impulse in u/s. 0 means it cannot jump. */
  jumpSpeed: number;
  /** Cells of wall it walks over without jumping. */
  stepUp: number;
  /** Cells it will voluntarily fall. */
  maxDrop: number;
  /** Highest `M.hardness` it can chew through, 0 = cannot dig. */
  digPower: number;
  /** Horizontal speed used for jump-range math. */
  runSpeed: number;
  /** Body half-extents, for probe placement. */
  halfW: number;
  halfH: number;
}

// ------------------------------------------------------------ cell helpers

/**
 * `cells.isSolid` is the authority, but it consults only resident chunks —
 * ungenerated space reads as non-solid. That is the behaviour we want: AI must
 * never force chunk generation, or a distant enemy pathfinds the world into
 * existence and the frame budget dies.
 */
export function solidAt(cells: CellWorld, x: number, y: number, budget: ProbeBudget): boolean {
  if (!budget.take()) return true; // out of budget: assume wall, stop moving
  return cells.isSolid(x, y);
}

/** Material at a cell, budgeted. Returns VOID when the budget is spent. */
export function matAt(cells: CellWorld, x: number, y: number, budget: ProbeBudget): MatId {
  if (!budget.take()) return VOID;
  return cells.peek(x, y);
}

/**
 * Can this body remove that cell? Reads `M.hardness` — the same gate weapons
 * use (`CellWorld.damageCell`), so a burrower's reach and a Breach module's
 * reach are described by one number and stay consistent by construction.
 */
export function canDigThrough(
  cells: CellWorld,
  x: number,
  y: number,
  digPower: number,
  budget: ProbeBudget,
): boolean {
  if (digPower <= 0) return false;
  const m = matAt(cells, x, y, budget);
  if (m === VOID) return true;
  if (M.indestructible[m] === 1) return false;
  return M.hardness[m] <= digPower;
}

/** True if a body of this density would sink in the cell (for swimmers). */
export function submergedIn(cells: CellWorld, x: number, y: number, budget: ProbeBudget): MatId {
  const m = matAt(cells, x, y, budget);
  if (m === VOID) return VOID;
  const cat = M.category[m];
  return cat === Category.LIQUID || cat === Category.SLUDGE ? m : VOID;
}

// ------------------------------------------------------- jump-arc feasibility

/** Apex height of a jump, in cells. h = v² / 2g. */
export function maxJumpHeight(jumpSpeed: number, riseGravity = MOVE.riseGravity): number {
  return (jumpSpeed * jumpSpeed) / (2 * riseGravity);
}

/**
 * Horizontal reach of a jump that ends `dy` cells below the take-off point
 * (negative dy = landing higher). Returns -1 when the landing point is simply
 * above the apex and therefore unreachable.
 *
 * Asymmetric gravity is the whole point of the movement package, so the arc is
 * integrated in two halves rather than assuming a parabola.
 */
export function jumpRange(
  jumpSpeed: number,
  dy: number,
  runSpeed: number,
  riseGravity = MOVE.riseGravity,
  fallGravity = MOVE.fallGravity,
): number {
  const apex = maxJumpHeight(jumpSpeed, riseGravity);
  const fallHeight = apex + dy;
  if (fallHeight < 0) return -1; // landing is above the apex — impossible
  const tRise = jumpSpeed / riseGravity;
  const tFall = Math.sqrt((2 * fallHeight) / fallGravity);
  return runSpeed * (tRise + tFall);
}

/**
 * Can this body clear a gap `gapCells` wide landing `dy` below take-off?
 * A 15% haircut on the computed range keeps AI from attempting jumps it makes
 * only on a perfect frame — an enemy that falls in the pit every third try
 * reads as broken, not as fallible.
 */
export function canJumpAcross(gapCells: number, dy: number, caps: NavCaps): boolean {
  if (caps.jumpSpeed <= 0) return false;
  const reach = jumpRange(caps.jumpSpeed, dy, caps.runSpeed);
  return reach > 0 && gapCells <= reach * 0.85;
}

/** Can this body reach `height` cells straight up? */
export function canJumpUp(height: number, caps: NavCaps): boolean {
  if (caps.jumpSpeed <= 0) return false;
  return maxJumpHeight(caps.jumpSpeed) >= height + 1;
}

/** Minimum impulse that clears `height`, capped at the body's maximum. */
export function jumpSpeedFor(height: number, caps: NavCaps): number {
  const need = Math.sqrt(2 * MOVE.riseGravity * Math.max(1, height + 2));
  return Math.min(caps.jumpSpeed, need);
}

// --------------------------------------------------------- region routing
//
// Issue #24. `StructureMap` was written by worldgen at generation time and read
// by nothing at runtime — 472 lines whose only consumer was the file that
// filled it. docs/01 describes it as the coarse structural layer that exists
// precisely so systems do not have to reason about individual cells, and this
// is the first system to take it up on that.
//
// WHY IT FIXES SOMETHING REAL. Everything above this line probes the live cell
// grid a handful of cells ahead, which is always correct about the world as it
// is and cannot see past a wall. So an enemy separated from the player by one
// slab walks into it, reports WALL_BLOCKED and stops — the player is fifteen
// cells away and the route is forty, and nothing in the game could find it.
// A block graph is exactly the coarse long-range layer that closes that gap,
// and it does not have to be re-derived when the world is destroyed, because a
// destroyed SHELL block is still labelled SHELL and the place it bounded is
// still a place (structure.ts header).
//
// WHY IT IS OPT-IN. Registered rather than passed, so no call site changes and
// nothing that does not register a map behaves any differently than it did.
// A navigation change that arrives silently in every test in the suite is not
// a change anyone can reason about.

/** Blocks the router will expand before it settles for its best guess. */
export const REGION_MAX_BLOCKS = 384;
/**
 * Below this the cell probes are simply better: they know about the ledge two
 * cells ahead and the block graph does not. 24 cells is three blocks.
 */
const REGION_MIN_DIST = 24;

let structure: StructureMap | null = null;

/** Point the navigator at a world's structural map, or `null` to unregister. */
export function setNavStructure(map: StructureMap | null): void {
  structure = map;
  routeCacheKeyA.fill(0);
}

export function navStructure(): StructureMap | null {
  return structure;
}

// Preallocated BFS state. This runs per enemy per tick, so the only allocation
// tolerated is the Map's internal growth, which stops after the first frame.
const seenIndex = new Map<number, number>();
const qx = new Int32Array(REGION_MAX_BLOCKS);
const qy = new Int32Array(REGION_MAX_BLOCKS);
const parent = new Int32Array(REGION_MAX_BLOCKS);

/**
 * Direct-mapped result cache. Twenty enemies converging on one player share a
 * destination block and cluster into a handful of source blocks, so a tiny
 * cache turns twenty searches a tick into one or two.
 */
const ROUTE_CACHE = 32;
const routeCacheKeyA = new Float64Array(ROUTE_CACHE);
const routeCacheKeyB = new Float64Array(ROUTE_CACHE);
const routeCacheX = new Int32Array(ROUTE_CACHE);
const routeCacheY = new Int32Array(ROUTE_CACHE);
const routeCacheOk = new Uint8Array(ROUTE_CACHE);

/** Same packing as structure.ts — the block range overflows 32-bit shifts. */
function bkey(bx: number, by: number): number {
  return (bx + 0x80000) * 0x100000 + (by + 0x80000);
}

/**
 * Next point to steer at on the structural route from one world point to
 * another. Writes into `out` and returns true when the graph had something to
 * say; on false `out` is the target itself, so a caller can use the result
 * unconditionally.
 *
 * When the destination is outside the expansion budget the search still
 * answers, with the visited block closest to it. An enemy that makes progress
 * around the corner and then re-decides is right for the same reason the cell
 * probes are: the world is being destroyed while it walks.
 */
export function navWaypoint(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  out: { x: number; y: number },
): boolean {
  out.x = toX;
  out.y = toY;
  const map = structure;
  if (map === null) return false;

  const sbx = Math.floor(fromX) >> BLOCK_SHIFT;
  const sby = Math.floor(fromY) >> BLOCK_SHIFT;
  const tbx = Math.floor(toX) >> BLOCK_SHIFT;
  const tby = Math.floor(toY) >> BLOCK_SHIFT;
  if (sbx === tbx && sby === tby) return false;

  const ka = bkey(sbx, sby);
  const kb = bkey(tbx, tby);
  const slot = (sbx * 31 + sby * 7 + tbx * 3 + tby) & (ROUTE_CACHE - 1);
  if (routeCacheKeyA[slot] === ka && routeCacheKeyB[slot] === kb) {
    if (routeCacheOk[slot] === 0) return false;
    out.x = routeCacheX[slot];
    out.y = routeCacheY[slot];
    return true;
  }

  const found = search(map, sbx, sby, tbx, tby);
  routeCacheKeyA[slot] = ka;
  routeCacheKeyB[slot] = kb;
  routeCacheOk[slot] = found >= 0 ? 1 : 0;
  if (found < 0) return false;

  // Walk the parent chain back to the block adjacent to the source: that, not
  // the destination, is the thing to steer at.
  let n = found;
  while (parent[n] > 0) n = parent[n];
  const wx = (qx[n] << BLOCK_SHIFT) + (BLOCK_SIZE >> 1);
  const wy = (qy[n] << BLOCK_SHIFT) + (BLOCK_SIZE >> 1);
  routeCacheX[slot] = wx;
  routeCacheY[slot] = wy;
  out.x = wx;
  out.y = wy;
  return true;
}

/**
 * Breadth-first over passable blocks. Returns the queue index of the
 * destination, or of the closest block reached, or -1 when the source itself
 * is not on passable structure.
 *
 * 4-connected, matching `StructureMap.verifyConnectivity`: a diagonal squeeze
 * through a block corner is not a route the player can walk either, and a
 * navigator that believes in one walks enemies into corners.
 */
function search(map: StructureMap, sbx: number, sby: number, tbx: number, tby: number): number {
  if (!isPassable(map.tagAtBlock(sbx, sby))) return -1;
  seenIndex.clear();
  seenIndex.set(bkey(sbx, sby), 0);
  qx[0] = sbx;
  qy[0] = sby;
  parent[0] = -1;
  let n = 1;
  let head = 0;
  let best = -1;
  let bestD = Infinity;

  while (head < n) {
    const bx = qx[head];
    const by = qy[head];
    for (let d = 0; d < 4; d++) {
      const nx = bx + (d === 0 ? 1 : d === 1 ? -1 : 0);
      const ny = by + (d === 2 ? 1 : d === 3 ? -1 : 0);
      const key = bkey(nx, ny);
      if (seenIndex.has(key)) continue;
      if (!isPassable(map.tagAtBlock(nx, ny))) continue;
      if (n >= REGION_MAX_BLOCKS) return best;
      seenIndex.set(key, n);
      qx[n] = nx;
      qy[n] = ny;
      parent[n] = head;
      if (nx === tbx && ny === tby) return n;
      const dd = (nx - tbx) * (nx - tbx) + (ny - tby) * (ny - tby);
      if (dd < bestD) {
        bestD = dd;
        best = n;
      }
      n++;
    }
    head++;
  }
  return best;
}

// ---------------------------------------------------------- ground-follow

/**
 * One step of walking navigation. Probes the column just ahead of the body for
 * a wall, then the column beyond the feet for a ledge, and decides between
 * walk / step-up / jump / dig / stop.
 *
 * Result lands in `NAV`. Probe cost: typically 6–20 cells, hard-capped by the
 * budget the caller passes in.
 */
export function groundFollow(
  cells: CellWorld,
  x: number,
  y: number,
  targetX: number,
  targetY: number,
  caps: NavCaps,
  budget: ProbeBudget,
): NavResult {
  resetNav();

  // Long range is the structural graph's business, short range is the probes'.
  // Re-target rather than re-plan: everything below still decides walk / jump /
  // dig from live cells, it just aims at the next room instead of through the
  // wall between here and the player (#24).
  let tx = targetX;
  let ty = targetY;
  if (structure !== null && Math.abs(targetX - x) + Math.abs(targetY - y) > REGION_MIN_DIST) {
    if (navWaypoint(x, y, targetX, targetY, WAYPOINT)) {
      tx = WAYPOINT.x;
      ty = WAYPOINT.y;
    }
  }

  const dir = tx > x + 1 ? 1 : tx < x - 1 ? -1 : 0;
  if (dir === 0) {
    NAV.reason = NavReason.ARRIVED;
    return NAV;
  }
  NAV.moveX = dir;

  const feetY = Math.floor(y + caps.halfH);
  const aheadX = Math.floor(x + dir * (caps.halfW + 1.5));

  // --- wall probe: how many solid cells are stacked in the column ahead?
  let wallHeight = 0;
  for (let h = 0; h < MAX_WALL_SCAN; h++) {
    if (!solidAt(cells, aheadX, feetY - h, budget)) break;
    wallHeight = h + 1;
    if (budget.exhausted) break;
  }

  if (wallHeight > 0) {
    if (wallHeight <= caps.stepUp) {
      NAV.reason = NavReason.STEP_UP;
      return NAV; // the body controller's step-up assist handles it
    }
    // Enough headroom above the wall to actually land on it?
    const clearAbove = !solidAt(cells, aheadX, feetY - wallHeight - Math.ceil(caps.halfH * 2), budget);
    if (clearAbove && canJumpUp(wallHeight, caps)) {
      NAV.jump = true;
      NAV.jumpSpeed = jumpSpeedFor(wallHeight, caps);
      NAV.reason = NavReason.WALL_JUMPABLE;
      return NAV;
    }
    // Burrowers and breachers eat the wall instead of going over it.
    const biteY = feetY - Math.min(wallHeight, Math.max(1, Math.floor(caps.halfH)));
    if (canDigThrough(cells, aheadX, biteY, caps.digPower, budget)) {
      NAV.dig = true;
      NAV.digX = aheadX;
      NAV.digY = biteY;
      NAV.reason = NavReason.WALL_DIGGABLE;
      return NAV;
    }
    NAV.blocked = true;
    NAV.moveX = 0;
    NAV.reason = NavReason.WALL_BLOCKED;
    return NAV;
  }

  // --- ledge probe: is there floor under the next step?
  let groundDrop = -1;
  for (let d = 1; d <= MAX_DROP_SCAN; d++) {
    if (solidAt(cells, aheadX, feetY + d, budget)) {
      groundDrop = d - 1;
      break;
    }
    if (budget.exhausted) break;
  }

  if (groundDrop === 0) {
    NAV.reason = NavReason.CLEAR;
    return NAV;
  }

  // A drop we're happy to take — and if the target is below us, taking it is
  // usually the whole plan.
  if (groundDrop > 0 && (groundDrop <= caps.maxDrop || ty > y + groundDrop)) {
    NAV.reason = NavReason.DROP_SAFE;
    return NAV;
  }

  // --- gap: scan forward for the far lip, then ask the arc solver.
  let gapWidth = 0;
  let landingDy = 0;
  let found = false;
  for (let g = 2; g <= MAX_GAP_SCAN && !budget.exhausted; g++) {
    const gx = aheadX + dir * g;
    // The far side's surface may be above or below our feet.
    for (let d = -caps.stepUp; d <= caps.maxDrop; d++) {
      if (solidAt(cells, gx, feetY + d, budget)) {
        gapWidth = g;
        landingDy = d - 1;
        found = true;
        break;
      }
    }
    if (found) break;
  }

  if (found && canJumpAcross(gapWidth, landingDy, caps)) {
    NAV.jump = true;
    NAV.jumpSpeed = caps.jumpSpeed;
    NAV.reason = NavReason.GAP_JUMPABLE;
    return NAV;
  }

  NAV.blocked = true;
  NAV.moveX = 0;
  NAV.reason = found ? NavReason.GAP_TOO_WIDE : NavReason.DROP_BOTTOMLESS;
  return NAV;
}

// ------------------------------------------------------------------ flying

/**
 * Fliers steer straight at the target and only deflect when a probe in the
 * direction of travel is solid. Three probes (ahead, ahead-high, ahead-low) are
 * enough to slide along a wall, which is all the finesse a flier needs when it
 * can also just go over the room.
 */
export function flyStep(
  cells: CellWorld,
  x: number,
  y: number,
  targetX: number,
  targetY: number,
  caps: NavCaps,
  budget: ProbeBudget,
): NavResult {
  resetNav();

  // Same two-scale split `groundFollow` makes: the structural graph answers
  // "which way out of this room", the cell probes answer "what is immediately
  // in front of me". Without it a flier steers at the straight line, finds the
  // slab, exhausts its three slide probes against a wall the route graph says
  // is passable, and reports `blocked` — which is how half the roster came to
  // pin itself to geometry walkers route around. Swimmers inherit this: every
  // `swimStep` is a `flyStep` plus buoyancy.
  let tx = targetX;
  let ty = targetY;
  if (structure !== null && Math.abs(targetX - x) + Math.abs(targetY - y) > REGION_MIN_DIST) {
    if (navWaypoint(x, y, targetX, targetY, WAYPOINT)) {
      tx = WAYPOINT.x;
      ty = WAYPOINT.y;
    }
  }

  let dx = tx - x;
  let dy = ty - y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-4) {
    NAV.reason = NavReason.ARRIVED;
    return NAV;
  }
  dx /= len;
  dy /= len;

  const lookX = Math.floor(x + dx * (caps.halfW + 3));
  const lookY = Math.floor(y + dy * (caps.halfH + 3));

  if (!solidAt(cells, lookX, lookY, budget)) {
    NAV.moveX = dx;
    NAV.moveY = dy;
    return NAV;
  }

  // Blocked ahead — try sliding perpendicular, preferring the side that keeps
  // us moving toward the target.
  const px = -dy;
  const py = dx;
  const upFirst = ty < y ? -1 : 1;
  for (const s of SLIDE_ORDER) {
    const sx = px * s * upFirst;
    const sy = py * s * upFirst;
    const tx = Math.floor(x + (dx + sx) * (caps.halfW + 3));
    const ty = Math.floor(y + (dy + sy) * (caps.halfH + 3));
    if (!solidAt(cells, tx, ty, budget)) {
      NAV.moveX = dx + sx;
      NAV.moveY = dy + sy;
      return NAV;
    }
    if (budget.exhausted) break;
  }

  if (canDigThrough(cells, lookX, lookY, caps.digPower, budget)) {
    NAV.dig = true;
    NAV.digX = lookX;
    NAV.digY = lookY;
    NAV.moveX = dx;
    NAV.moveY = dy;
    NAV.reason = NavReason.WALL_DIGGABLE;
    return NAV;
  }

  NAV.blocked = true;
  NAV.reason = NavReason.WALL_BLOCKED;
  return NAV;
}

/** The one waypoint the ground-follower steers at. Singleton, like `NAV`. */
const WAYPOINT = { x: 0, y: 0 };

/** Pre-built so the flier's slide search allocates nothing. */
const SLIDE_ORDER = [1, -1] as const;

// ----------------------------------------------------------------- swimming

/**
 * Swimmers navigate like fliers but are pulled toward their own equilibrium
 * depth: a body denser than the fluid sinks and must swim up, a lighter one
 * bobs. Reading `M.density` here is what makes a fen leech behave differently
 * in water than in plague slurry without a line of code about either.
 */
export function swimStep(
  cells: CellWorld,
  x: number,
  y: number,
  targetX: number,
  targetY: number,
  bodyDensity: number,
  caps: NavCaps,
  budget: ProbeBudget,
): NavResult {
  const r = flyStep(cells, x, y, targetX, targetY, caps, budget);
  const fluid = submergedIn(cells, Math.floor(x), Math.floor(y), budget);
  if (fluid === VOID) {
    // Out of its element: fall, and drive hard toward the target horizontally.
    r.moveY += 0.6;
    return r;
  }
  const fluidDensity = M.density[fluid];
  // >0 sinks, <0 floats. Viscosity damps how strongly it can fight that.
  const buoyancy = (bodyDensity - fluidDensity) / Math.max(1, fluidDensity);
  const authority = 1 - Math.min(0.8, M.viscosity[fluid]);
  r.moveY += buoyancy * 0.5 * authority;
  r.moveX *= authority;
  return r;
}

// -------------------------------------------------------------------- sight

interface LosState {
  blocked: boolean;
  opacity: number;
  limit: number;
}
const losState: LosState = { blocked: false, opacity: 0, limit: 1 };

/**
 * Defined once at module scope: a closure created per raycast would be an
 * allocation per enemy per tick, which is exactly the kind of thing that shows
 * up as a sawtooth in the heap graph and nowhere in the profile.
 */
function losHit(_x: number, _y: number, mat: MatId, _dist: number): boolean {
  if (mat === VOID) return false;
  losState.opacity += M.opacity[mat];
  if (losState.opacity >= losState.limit) {
    losState.blocked = true;
    return true;
  }
  return false;
}

/**
 * Line of sight through *accumulated* opacity rather than a binary wall test,
 * so smoke and steam genuinely hide you and a pane of glass does not. Returns
 * true if the target is visible.
 */
export function lineOfSight(
  cells: CellWorld,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  opacityLimit = 1,
): boolean {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dist = Math.hypot(dx, dy);
  if (dist < 1) return true;
  losState.blocked = false;
  losState.opacity = 0;
  losState.limit = opacityLimit;
  cells.raycast(x0, y0, dx, dy, dist, losHit);
  return !losState.blocked;
}

/** How much cover is between two points, 0 = clear. Used for zoner repositioning. */
export function occlusion(cells: CellWorld, x0: number, y0: number, x1: number, y1: number): number {
  lineOfSight(cells, x0, y0, x1, y1, 1e9);
  return losState.opacity;
}

// ------------------------------------------------------------------- ground

/**
 * Distance from a point down to the first solid cell, capped. -1 if none found.
 * Anchors use it to verify they are still on their terrain; shapers use it to
 * find the floor they intend to flood.
 */
export function groundBelow(
  cells: CellWorld,
  x: number,
  y: number,
  maxScan: number,
  budget: ProbeBudget,
): number {
  for (let d = 0; d <= maxScan; d++) {
    if (solidAt(cells, x, y + d, budget)) return d;
    if (budget.exhausted) return -1;
  }
  return -1;
}

/** Distance up to the first solid cell (a ceiling to pour liquid from). -1 if open. */
export function ceilingAbove(
  cells: CellWorld,
  x: number,
  y: number,
  maxScan: number,
  budget: ProbeBudget,
): number {
  for (let d = 0; d <= maxScan; d++) {
    if (solidAt(cells, x, y - d, budget)) return d;
    if (budget.exhausted) return -1;
  }
  return -1;
}
